import React, { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { IFetchCategory } from '../../typings/ICategory';
import TabsList from '../common/TabList';
import ProductLoader from '../Loaders/ProductLoader';
import OwnCategoriesComponent from '../common/CustomSlider';
import GoBackBtn from '../CategoriesMobile/GoBackBtn';
import FilterComponent from '../common/Filters/FilterComponent';
import { FILTER_TYPE, IFiltersDTO } from '../../typings/IFilters';
import Products from '../Products/Products';
import { selectorsFilters } from '../../redux/filters/filtersReducer';
import { useSelector } from 'react-redux';

import { useNavigate, useNavigationType, useLocation } from 'react-router';
import isLocationForFirstLoadedPage from '../../utils/isLocationForFirstLoadedPage';
import { selectorsLocale } from '../../redux/locale/localeReducer';
import { selectorsOther } from '../../redux/other/otherReducer';
import { useLessThen991 } from '../../utils/mediaQuery';
import useFilterQueryParams from '../../useHooks/useFilterQueryParams';
import useFilterDataFromSearchParams from '../../useHooks/useFilterDataFromSearchParams';
import queries from '../../services/queries';
import useApi from 'react-use-api';
import { selectorsDelivery } from '../../redux/delivery/deliveryReducer';
import { EventSourcesData } from '../../utils/DataLayer/constant';

interface ICategoryFirstLevelProps {
  currentCategory: IFetchCategory;
  setIsLoading: any;
  query: any;
  chainsCategory: IFetchCategory[];
  startPage: number;
}

const CategoryFirstLevel = (props: ICategoryFirstLevelProps) => {
  const { currentCategory, chainsCategory, query, startPage } = props;
  const isCategoryIdNan = isNaN(currentCategory?.id);
  const totalCount = useSelector(selectorsFilters.getTotalSelectedFiltersCount);
  const selectedBrands = useSelector(selectorsFilters.getSelectedBrands);
  const selectedBadges = useSelector(selectorsFilters.getSelectedBadges);
  const selectedAllergich = useSelector(selectorsFilters.getSelectedAllergich);
  const selectedNutrion = useSelector(selectorsFilters.getSelectedNutrion);
  const hasPriceRanges = useSelector(selectorsFilters.getHasPriceRanges);
  const hasBadges = useSelector(selectorsFilters.getHasBadges);
  const hasSalePrice = useSelector(selectorsFilters.getHasSalePrice);
  const searchFilter = useSelector(selectorsFilters.getSearchFilter);
  const isDesktop = useSelector(selectorsOther.isDesktop);
  const isInCourierArea = useSelector(selectorsDelivery.isInCourierArea);
  const isLessThen991 = useLessThen991(isDesktop);
  const [sortingData, setSortingData] = useState<undefined | string>(undefined);
  const [productsTrigger, setProductsTrigger] = useState(false);

  const navigationType = useNavigationType();
  const location = useLocation();
  const navigate = useNavigate();
  const translate = useSelector(selectorsLocale.getTranslate);

  const SLIDERS_LIMIT = 4;
  const PRODUCT_ONLY_LEVEL = 3;
  const isProductCategoriesLevel = chainsCategory?.length !== PRODUCT_ONLY_LEVEL;

  const getActualFilters = () => {
    return {
      brands: selectedBrands?.length ? selectedBrands.slice(0).map((brand) => brand.id) : null,
      badges: selectedBadges?.length ? selectedBadges.slice(0).map((badge) => badge.id) : null,
      selectedAllergich: selectedAllergich,
      selectedNutrion: selectedNutrion,
      hasPriceRanges: hasPriceRanges,
      hasBadges: hasBadges,
      hasSalePrice: hasSalePrice,
      searchFilter: searchFilter || '',
    };
  };

  const { brandsQuery, badgesQuery, attributesQuery } = useFilterQueryParams({
    type: FILTER_TYPE.OWNCATEGORY,
    id: currentCategory.id,
    filters: {
      ...({} as IFiltersDTO),
      ...getActualFilters(),
    },
  });
  const { brands, badges, allergich, nutrion, searchQueryData } = useFilterDataFromSearchParams(
    brandsQuery,
    badgesQuery,
    attributesQuery,
  );

  const backBtn = useMemo(() => {
    if (isLocationForFirstLoadedPage(location.key, navigationType)) {
      return '';
    }
    if (!isLessThen991) {
      return '';
    }
    return <GoBackBtn location={location} navigate={navigate} translate={translate} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleData = (data: IFetchCategory[], state: any) => {
    const { error } = state;
    if (error) {
      return [];
    }
    data.sort((a, b) => a.ord - b.ord);

    return [...data];
  };

  const getCustomCategories = (id: number) =>
    queries.getChildCustomCategoriesForCustomCategory(id, getActualFilters(), isInCourierArea);

  const [ownCategories, { loading: isLoadingOwnCategories }] = useApi(getCustomCategories(currentCategory.id), {
    skip: isCategoryIdNan,
    handleData,
  });
  const [isHasMore, setIsHasMore] = useState(ownCategories?.length ? SLIDERS_LIMIT < ownCategories?.length : false);
  const [limit, setLimit] = useState(SLIDERS_LIMIT);
  const [shownCategories, setShownCategories] = useState<IFetchCategory[]>(
    ownCategories?.length ? ownCategories?.slice(0, limit) : [],
  );

  useEffect(() => {
    setProductsTrigger(!productsTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  useEffect(() => {
    setShownCategories(ownCategories?.length ? ownCategories : []);
  }, [ownCategories]);

  const changeLimit = () => {
    if (!shownCategories?.length) {
      return;
    }
    if (shownCategories.length >= ownCategories.length || 0) {
      setIsHasMore(false);
    } else {
      const newLimit = limit + 1;
      setLimit(newLimit);
      setShownCategories(ownCategories.slice(0, newLimit));
    }
  };

  const applyFilters = () => {
    setProductsTrigger(!productsTrigger);
  };

  const setSort = (sortData: string | undefined) => {
    setSortingData(sortData);
    applyFilters();
  };

  return (
    <section className="category-first">
      {backBtn}
      <div className="container">
        {!!ownCategories?.length && <TabsList items={ownCategories} />}
        {isProductCategoriesLevel && (
          <FilterComponent
            id={currentCategory.id}
            type={FILTER_TYPE.OWNCATEGORY}
            applyFilters={applyFilters}
            sortProducts={setSort}
            filters={getActualFilters()}
            brands={brands}
            badges={badges}
            allergich={allergich}
            nutrion={nutrion}
            searchQueryData={searchQueryData}
          />
        )}
      </div>
      {!isLoadingOwnCategories && shownCategories.length > 0 && isProductCategoriesLevel && (
        <InfiniteScroll
          dataLength={shownCategories.length}
          style={{ overflowX: 'hidden' }}
          next={() => changeLimit()}
          hasMore={isHasMore}
          loader={<ProductLoader />}>
          {shownCategories.map((category: IFetchCategory) => {
            return (
              <OwnCategoriesComponent
                key={category.id}
                id={category.id}
                name={category.name}
                parentId={currentCategory.id}
                setLimit={changeLimit}
                filters={getActualFilters()}
                sortingData={sortingData}
                dlEcommerceEventSource={EventSourcesData.PRODUCTS_SLIDER}
              />
            );
          })}
        </InfiniteScroll>
      )}

      {!isProductCategoriesLevel && (
        <Products
          className="col-12 catalog-right"
          query={query}
          seoText={startPage === 1 ? currentCategory.seoText! : undefined}
          hideBackBtn={true}
        />
      )}
    </section>
  );
};

export default CategoryFirstLevel;
